// src/pages/inventory/ProductImport.jsx
import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import LoaderSpiner from "../../components/LoaderSpiner/LoaderSpiner";
import ThemeMaker from "../../components/thememaker";
import { VscDash } from "react-icons/vsc";
import { BsCheck2 } from "react-icons/bs";
import { httpService } from "../../services/httpService";

const ProductImport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isFile, setIsFile] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedExtensions = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/csv",
      ];
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (
        allowedExtensions.includes(file.type) ||
        fileExtension === "csv" ||
        fileExtension === "xls" ||
        fileExtension === "xlsx"
      ) {
        setIsFile(true);
        setSelectedFile(file);
      } else {
        toast.warn("Please upload only Excel or CSV files.", {
          position: "top-center",
        });
        event.target.value = "";
      }
    }
  };

  const processFile = async () => {
    if (!selectedFile) return;
    const formData = new FormData();
    formData.append("file", selectedFile);
    
    try {
      setLoading(true);
      const response = await httpService.post("/vendorproduct/importVendorProducts", formData);
      
      if (response.status) {
        const excelData = response?.data;
        setLoading(false);
        navigate("/admin/inventory-management/product-import-preview", {
          state: {
            excelData,
            fileName: selectedFile ? selectedFile?.name : null,
          },
        });
      } else {
        setLoading(false);
        toast.warn(response.data.message, { position: "top-center" });
      }
    } catch (error) {
      setLoading(false);
      toast.error("Error importing products: " + error.message, {
        position: "top-center",
      });
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      const allowedExtensions = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/csv",
      ];
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (
        allowedExtensions.includes(file.type) ||
        fileExtension === "csv" ||
        fileExtension === "xls" ||
        fileExtension === "xlsx"
      ) {
        setIsFile(true);
        setSelectedFile(file);
      } else {
        toast.warn("Please upload only Excel or CSV files.", {
          position: "top-center",
        });
      }
    }
  };

  const handleClosePop = () => {
    setIsFile(false);
    setSelectedFile(null);
  };

  // ✅ Download sample template from public folder
  const downloadSampleTemplate = () => {
    const link = document.createElement('a');
    link.href = '/ProductSample.csv';  // Your filename
    link.download = 'product_import_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      {loading === true ? <LoaderSpiner text="Loading . . ." /> : ""}
      <ThemeMaker>
        {loading && <LoaderSpiner text="Loading . . ." />}
        
        <div className="flex flex-col items-center justify-center my-8">
          <>
            {isFile && selectedFile ? (
              <div className="text-center w-1/5">
                <h1 className="text-center text-xl font-semibold my-6">
                  File Uploaded
                </h1>
                <button className="border-4 border-green-500 rounded-full">
                  <BsCheck2 className="text-green-500 text-[60px] p-1 font-semibold" />
                </button>
                <h2 className="text-lg text-gray-500 font-semibold mb-8">
                  {selectedFile?.name}
                </h2>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                />
                <button
                  className="px-4 py-1.5 text-center mb-6 bg-blue-600 text-white rounded-md shadow-md hover:bg-blue-700 transition-all duration-300"
                  onClick={triggerFileInput}
                >
                  Change File
                </button>
                <div className="flex justify-between mt-5">
                  <button
                    className="px-4 py-1.5 text-white bg-red-500 rounded-md mr-2"
                    onClick={handleClosePop}
                  >
                    Back
                  </button>
                  <button
                    className="px-4 py-1.5 bg-blue-600 text-white rounded-md"
                    onClick={processFile}
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex justify-center flex-col">
                <h1 className="text-2xl font-bold mb-4 text-center">
                  Choose file to upload
                </h1>
                
                {/* ✅ Sample file link using your file */}
                <div className="mb-4 text-center">
                  <ul>
                    <li>
                      <a
                        href="/ProductSample.csv"
                        download
                        className="text-blue-500 underline text-lg font-semibold"
                      >
                        Click here
                      </a>{" "}
                      to download a Sample file
                    </li>
                  </ul>
                </div>

                <div
                  className={`flex justify-center items-center py-8 px-10 border-2 ${
                    dragActive
                      ? "border-blue-600 text-blue-600"
                      : "border-gray-400"
                  } border-dashed rounded-md text-center mb-6`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <p className="text-gray-600">
                    {dragActive
                      ? "Drop your file here . . ."
                      : "Drag & drop a csv or excel file"}
                  </p>
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                />
                <button
                  className="px-4 py-2 text-center bg-blue-600 text-white rounded-md shadow-md hover:bg-blue-700 transition-all duration-300"
                  onClick={triggerFileInput}
                >
                  Select a file
                </button>
              </div>
            )}
          </>
        </div>
        
        <div className="flex justify-start mt-auto">
          <div className="p-4 w-full">
            <h2 className="font-semibold text-xl ml-2">Tips for Imports</h2>
            <h3 className="flex items-center">
              <VscDash className="text-lg font-bold" />
              Product Name, SKU Number, Unit, GST Slab, MRP, Purchase Price, and Sale Price are required
            </h3>
            <h3 className="flex items-center">
              <VscDash className="text-lg font-bold" />
              SKU Number must be unique for each product
            </h3>
            <h3 className="flex items-center">
              <VscDash className="text-lg font-bold" />
              For medicine products, Batch Number and Expiry Date are required
            </h3>
          </div>
        </div>
      </ThemeMaker>
    </>
  );
};

export default ProductImport;